/**
 * NoteItemView - Displays a single note in read-only mode
 *
 * Renders a note's author, date, sharing badge, action buttons (for owned
 * notes), and body text. Editing is handled by the NoteEditorModal (SE-5519) —
 * pressing edit opens that modal rather than swapping this item for an inline
 * form. Used in both the main notes list and the "View All" modal to eliminate
 * duplication of the note rendering logic.
 * @remarks This is a View component - contains no business logic, hooks, or state
 */

import React, { memo } from "react";

import type { PlayerNote } from "@/features/player-detail/types";

/** Supported preview-line clamp values — kept in sync with PREVIEW_CLAMP_CLASS below. */
type PreviewLines = 1 | 2 | 3;

import { Box } from "@/components/atoms/Box";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

import NoteActionButtons from "@/features/player-detail/components/molecules/NoteActionButtons";

/**
 * Props for NoteItemView component
 */
interface NoteItemViewProps {
  /** The note to display */
  readonly note: PlayerNote;
  /** Whether dark mode is active */
  readonly isDark: boolean;
  /** Whether this note is owned by the current user */
  readonly isOwned: boolean;
  /** Callback when edit note is pressed */
  readonly onEditNote: (noteId: string) => void;
  /** Callback when delete note is pressed */
  readonly onDeleteNote: (noteId: string) => void;
  /** Callback when share note is pressed */
  readonly onShareNote: (noteId: string) => void;
  /** Whether note sharing is available (false when org defaults to public visibility) */
  readonly canShareNotes?: boolean;
  /** When set, clamp the note body to this many lines with a trailing ellipsis
   *  (list-preview surfaces). Omit for full content (e.g. the View All modal). */
  readonly previewLines?: PreviewLines;
}

/**
 * Web fallback for the preview clamp (SE-5579): on web the Text primitive
 * renders a raw span, which silently drops RN's `numberOfLines` — so the
 * clamp must also be applied as CSS. Closed map (not a template literal)
 * because Tailwind only compiles statically analyzable class names.
 * `numberOfLines` stays on the element for native, where the CSS clamp is
 * inert. (The pre-fix defect, observed on the staging list card: a long note
 * rendered its full body on web despite `numberOfLines={2}`.)
 */
const PREVIEW_CLAMP_CLASS: Readonly<Record<PreviewLines, string>> = {
  1: "web:line-clamp-1",
  2: "web:line-clamp-2",
  3: "web:line-clamp-3",
};

/**
 * NoteItemView renders a single note in read-only mode
 * @param props - View props containing note data and callbacks
 * @param props.note - The note to display (includes shareCount and isSharedWithMe)
 * @param props.isDark - Whether dark mode is active
 * @param props.isOwned - Whether this note is owned by the current user
 * @param props.onEditNote - Callback when edit note is pressed
 * @param props.onDeleteNote - Callback when delete note is pressed
 * @param props.onShareNote - Callback when share note is pressed
 * @param props.canShareNotes - Whether note sharing is available (false when org defaults to public visibility)
 * @param props.previewLines - When set, clamp the note body to this many lines with a trailing ellipsis (omit for full content)
 * @returns Stack containing the note's read-only display
 */
const NoteItemView = ({
  note,
  isDark,
  isOwned,
  onEditNote,
  onDeleteNote,
  onShareNote,
  canShareNotes,
  previewLines,
}: NoteItemViewProps) => (
  <Stack className="gap-2">
    <Stack direction="horizontal" className="items-center justify-between">
      <Stack
        direction="horizontal"
        className="flex-1 flex-wrap items-center gap-2"
      >
        <Text variant="caption" className="font-medium text-content-secondary">
          {note.authorName}
        </Text>
        <Text variant="caption" className="text-content-muted">
          ·
        </Text>
        <Text variant="caption" className="text-content-muted">
          {note.date}
        </Text>
        {note.isSharedWithMe && !isOwned && (
          <Box className="rounded-sm bg-status-warning-surface px-2 py-micro">
            <Text
              variant="caption"
              className="font-medium uppercase text-status-warning"
            >
              Shared
            </Text>
          </Box>
        )}
      </Stack>
      {isOwned && (
        <NoteActionButtons
          noteId={note.id}
          isDark={isDark}
          shareCount={note.shareCount}
          onEditNote={onEditNote}
          onDeleteNote={onDeleteNote}
          onShareNote={onShareNote}
          showShareButton={note.isPrivate && canShareNotes !== false}
        />
      )}
    </Stack>
    <Text
      variant="title-sm"
      // SE-5597: `break-words` (overflow-wrap) + `whitespace-normal` so long
      // unbroken strings (e.g. a run of digits with no spaces) wrap to the
      // container width instead of being clipped at its edge — the "All Notes"
      // modal (previewLines omitted) renders the full body, and this makes
      // "full" actually readable. Harmless in the clamped list variant, where
      // web:line-clamp still truncates. Repo precedent: PlayerComparisonView /
      // RosterOverviewView.
      className={`whitespace-normal break-words text-content-secondary ${
        previewLines != null ? (PREVIEW_CLAMP_CLASS[previewLines] ?? "") : ""
      }`.trim()}
      numberOfLines={previewLines}
      ellipsizeMode="tail"
    >
      {note.content}
    </Text>
  </Stack>
);

NoteItemView.displayName = "NoteItemView";

export default memo(NoteItemView);
